import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { config } from '../../config/environment';
import { useAuth, useDrawer } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  LinearProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Comment as CommentIcon,
  Bookmark as BookmarkIcon,
  Description as DescriptionIcon,
  Note as NoteIcon,
  InsertDriveFile as InsertDriveFileIcon,
  NoteAdd as NoteAddIcon,
  SmartToy as SmartToyIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import apiService, { DocumentData, Note, DocumentPreviewInfo } from '../../services/api';
import { Annotation, TextAnnotation, DEFAULT_ANNOTATION_STYLE } from './types/AnnotationTypes';
import SmartDocumentViewer from './SmartDocumentViewer';
import AIChatInterface from './AIChatInterface';
import geminiService, { DocumentAnalysis, ChatSession, ChatMessage } from '../../services/geminiService';
import jsPDF from 'jspdf';

// Convert API annotation to viewer annotation
function convertApiAnnotationToViewer(apiAnnotation: any): Annotation {
  console.log('🔄 Converting API annotation:', apiAnnotation);
  console.log('🆔 API annotation ID:', apiAnnotation.id);
  
  try {
    // Try to parse the text as JSON to get the full annotation data
    const fullData = JSON.parse(apiAnnotation.text);
    console.log('📄 Parsed full data:', fullData);
    
    // If it's valid JSON with our annotation data, use it
    if (fullData.type && fullData.x !== undefined && fullData.y !== undefined) {
      // Manejar la fecha correctamente
      let createdAt: Date;
      if (fullData.createdAt) {
        // Si tenemos la fecha guardada en el JSON, usarla
        createdAt = new Date(fullData.createdAt);
      } else if (apiAnnotation.timestamp) {
        // Fallback al timestamp del API
        createdAt = new Date(apiAnnotation.timestamp);
      } else {
        // Si no hay fecha, usar la fecha actual
        createdAt = new Date();
      }
      
      // Generate a unique ID if it doesn't exist or is too simple
      let uniqueId = apiAnnotation.id;
      if (!uniqueId || uniqueId === '0' || uniqueId === '1' || uniqueId === '2') {
        // Intentar usar el ID guardado en los datos JSON
        if (fullData.id && fullData.id !== '0' && fullData.id !== '1' && fullData.id !== '2') {
          uniqueId = fullData.id;
          console.log('🆔 Using ID from JSON data:', uniqueId);
        } else {
          uniqueId = `${fullData.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          console.log('🆔 Generated unique ID:', uniqueId, 'for type:', fullData.type);
        }
      } else {
        console.log('🆔 Using existing ID:', uniqueId);
      }
      
      const convertedAnnotation = {
        id: uniqueId,
        type: fullData.type,
        x: fullData.x,
        y: fullData.y,
        page: fullData.page || 1,
        style: fullData.style || DEFAULT_ANNOTATION_STYLE,
        createdAt: createdAt,
        createdBy: fullData.createdBy || apiAnnotation.author,
        // Type-specific data
        ...(fullData.type === 'text' && { text: fullData.text }),
        ...(fullData.type === 'freehand' && { 
          points: fullData.points || [],
          note: fullData.note || ''
        }),
        ...(fullData.type === 'highlight' && { 
          width: fullData.width || 100,
          height: fullData.height || 50,
          opacity: fullData.opacity || 0.6,
          note: fullData.note || ''
        }),
        ...(fullData.type === 'shapes' && { 
          shapeType: fullData.shapeType || 'rectangle',
          width: fullData.width || 100,
          height: fullData.height || 50,
          note: fullData.note || ''
        }),
      } as Annotation;
      
      console.log('✅ Converted annotation:', convertedAnnotation);
      return convertedAnnotation;
    }
  } catch (error) {
    // If JSON parsing fails, it's a legacy annotation
    console.log('Legacy annotation format detected:', apiAnnotation.text);
  }
  
  // Fallback to text annotation for legacy data
  let createdAt: Date;
  if (apiAnnotation.timestamp) {
    createdAt = new Date(apiAnnotation.timestamp);
  } else {
    createdAt = new Date();
  }
  
  // Generate a unique ID for legacy annotations
  let uniqueId = apiAnnotation.id;
  if (!uniqueId || uniqueId === '0' || uniqueId === '1' || uniqueId === '2') {
    uniqueId = `text-legacy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('🆔 Generated unique ID for legacy:', uniqueId);
  }
  
  const legacyAnnotation = {
    id: uniqueId,
    type: 'text',
    x: apiAnnotation.position.x,
    y: apiAnnotation.position.y,
    page: 1,
    style: DEFAULT_ANNOTATION_STYLE,
    createdAt: createdAt,
    createdBy: apiAnnotation.author,
    text: apiAnnotation.text,
  } as TextAnnotation;
  
  console.log('✅ Converted legacy annotation:', legacyAnnotation);
  return legacyAnnotation;
}

// Convert viewer annotation to API annotation
function convertViewerAnnotationToApi(viewerAnnotation: Annotation): any {
  console.log('🔄 Converting viewer annotation to API:', viewerAnnotation);
  console.log('🆔 Viewer annotation ID:', viewerAnnotation.id);
  
  // Store the full annotation data as JSON in the text field
  const fullAnnotationData = {
    id: viewerAnnotation.id, // Incluir el ID en los datos guardados
    type: viewerAnnotation.type,
    x: viewerAnnotation.x,
    y: viewerAnnotation.y,
    page: viewerAnnotation.page,
    style: viewerAnnotation.style,
    createdBy: viewerAnnotation.createdBy,
    createdAt: viewerAnnotation.createdAt.toISOString(), // Guardar la fecha como string
    // Type-specific data
    ...(viewerAnnotation.type === 'text' && { text: (viewerAnnotation as any).text }),
    ...(viewerAnnotation.type === 'freehand' && { 
      points: (viewerAnnotation as any).points,
      note: (viewerAnnotation as any).note 
    }),
    ...(viewerAnnotation.type === 'highlight' && { 
      width: (viewerAnnotation as any).width,
      height: (viewerAnnotation as any).height,
      opacity: (viewerAnnotation as any).opacity,
      note: (viewerAnnotation as any).note 
    }),
    ...(viewerAnnotation.type === 'shapes' && { 
      shapeType: (viewerAnnotation as any).shapeType,
      width: (viewerAnnotation as any).width,
      height: (viewerAnnotation as any).height,
      note: (viewerAnnotation as any).note 
    }),
  };
  
  const apiAnnotation = {
    text: JSON.stringify(fullAnnotationData),
    position: { x: viewerAnnotation.x, y: viewerAnnotation.y },
    author: viewerAnnotation.createdBy,
  };
  
  console.log('✅ Converted to API annotation:', apiAnnotation);
  return apiAnnotation;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Helper to parse multipart info
function parseInfoComponents(rawInfo: string) {
  if (!rawInfo) return [];
  const boundaryMatch = rawInfo.match(/^-+([A-Za-z0-9]+)/m);
  if (!boundaryMatch) return [];
  const boundary = boundaryMatch[0];
  const parts = rawInfo.split(boundary).map(p => p.trim()).filter(Boolean);
  return parts.map(part => {
    const headers: Record<string, string> = {};
    part.split(/\r?\n/).forEach(line => {
      const [key, ...rest] = line.split(':');
      if (rest.length) headers[key.trim()] = rest.join(':').trim();
    });
    return headers;
  }).filter(h => h['X-compId']);
}

const getIcon = (compId: string, contentType: string) => {
  if (compId === 'data' && contentType.includes('pdf')) return <DescriptionIcon color="primary" />;
  if (compId === 'note') return <NoteIcon color="secondary" />;
  if (compId === 'anno') return <NoteAddIcon color="action" />;
  return <InsertDriveFileIcon color="action" />;
};

const getTypeLabel = (compId: string, contentType: string) => {
  if (compId === 'data' && contentType.includes('pdf')) return 'PDF Document';
  if (compId === 'note') return 'Note';
  if (compId === 'anno') return 'Annotation';
  if (contentType) return contentType;
  return 'Unknown';
};

const formatLength = (lengthStr: string) => {
  const length = parseInt(lengthStr, 10);
  if (isNaN(length)) return lengthStr || '-';
  if (length < 1024) return `${length} B`;
  if (length < 1024 * 1024) return `${(length / 1024).toFixed(1)} KB`;
  return `${(length / (1024 * 1024)).toFixed(2)} MB`;
};

const MetadataTab: React.FC<{ rawInfo: string }> = ({ rawInfo }) => {
  const components = parseInfoComponents(rawInfo);
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Components</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Icon</TableCell>
              <TableCell>Component Name</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell>Length</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.map((c, idx) => (
              <TableRow key={idx}>
                <TableCell>{getIcon(c['X-compId'], c['Content-Type'] || '')}</TableCell>
                <TableCell>{c['X-compId'] === 'anno' ? 'Annotation' : c['X-compId']}</TableCell>
                <TableCell>{c['X-compDateC']} {c['X-compTimeC']}</TableCell>
                <TableCell>{c['X-compDateM']} {c['X-compTimeM']}</TableCell>
                <TableCell>{formatLength(c['X-Content-Length'])}</TableCell>
                <TableCell>{getTypeLabel(c['X-compId'], c['Content-Type'] || '')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {components.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No components found in info.
        </Typography>
      )}
    </Box>
  );
};

// Helper to parse notes
function parseNotes(rawNotes: string) {
  if (!rawNotes) return [];
  // Split by lines, filter empty
  const lines = rawNotes.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const notes = [];
  for (let i = 0; i < lines.length; i += 2) {
    const text = lines[i] || '';
    const meta = lines[i + 1] || '';
    // Try to extract time and user from meta
    let time = '', user = '';
    if (meta) {
      const match = meta.match(/^(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2} [A-Z]+)\s+(.+)$/);
      if (match) {
        time = match[1];
        user = match[2];
      } else {
        time = meta;
      }
    }
    notes.push({ text, time, user });
  }
  return notes;
}

const AISummary: React.FC<{ 
  documentData: DocumentData | null;
  onStartChat: () => void;
  onAnalyzeDocument: () => void;
  analysis: DocumentAnalysis | null;
  isLoading: boolean;
  error: string | null;
}> = ({ documentData, onStartChat, onAnalyzeDocument, analysis, isLoading, error }) => {
  const hasDocument = !!documentData?.binaryData;
  
  console.log('🤖 AISummary render:', {
    hasDocument,
    documentData: documentData ? 'exists' : 'null',
    binaryData: documentData?.binaryData ? 'exists' : 'null',
    binaryDataLength: documentData?.binaryData?.byteLength,
    analysis: analysis ? 'exists' : 'null',
    analysisSummary: analysis?.summary?.substring(0, 50) + '...',
    isLoading,
    error
  });

  return (
    <Card sx={{ m: 2, p: 2, borderRadius: 3, boxShadow: 2, height: 'calc(100% - 32px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', background: theme => theme.palette.grey[50] }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <SmartToyIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
        <Typography variant="h6" color="primary.main">AI Summary</Typography>
      </Box>
      
      <Box sx={{ flex: 1 }}>
        {!hasDocument ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Load a document to get AI analysis
          </Typography>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Analyzing document...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : analysis ? (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>Summary:</b> {analysis.summary}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>Classification:</b> <span style={{ color: '#1976d2', fontWeight: 500 }}>{analysis.classification}</span>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>Document Type:</b> {analysis.documentType}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <b>Key Topics:</b> {analysis.keyTopics.join(', ')}
            </Typography>
            <Chip 
              label={`Confidence: ${Math.round(analysis.confidence * 100)}%`}
              color="success"
              size="small"
              sx={{ mb: 1 }}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click "Analyze" to get a document summary
          </Typography>
        )}
      </Box>
      
      <Stack spacing={1}>
        {hasDocument && !analysis && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Analysis will start automatically...
            </Typography>
          </Box>
        )}
        
        {analysis && (
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            onClick={onStartChat}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Continue to Chat
          </Button>
        )}
      </Stack>
    </Card>
  );
};

const NotesTab: React.FC<{ rawNotes: string, currentQueryParams: string | null, reloadNotes: (q: string) => void, localNotes: any[], onAddNote: () => void, onUpdateNote: (id: string) => void, onDeleteNote: (id: string) => void, newNoteContent: string, setNewNoteContent: (content: string) => void }> = ({ rawNotes, currentQueryParams, reloadNotes, localNotes, onAddNote, onUpdateNote, onDeleteNote, newNoteContent, setNewNoteContent }) => {
  const notes = localNotes; // Use local notes instead of parsed rawNotes
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [deletingIdx, setDeletingIdx] = React.useState<number | null>(null);

  // Function to add a note - now uses parent component's local functions
  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      setError('Please fill in content');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      // Use parent component's addNote function
      await onAddNote();
      setSuccess(true);
    } catch (e: any) {
      setError('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  // Function to delete a note
  const handleDeleteNote = async (idx: number) => {
    if (!currentQueryParams) return;
    setDeletingIdx(idx);
    setError(null);
    try {
      await apiService.deleteNote(currentQueryParams, idx);
      await reloadNotes(currentQueryParams);
    } catch (e) {
      setError('Failed to delete note');
    } finally {
      setDeletingIdx(null);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1}>
        {notes.length === 0 && (
          <Typography variant="body2" color="text.secondary">No notes available.</Typography>
        )}
        {notes.map((note, idx) => (
          <Card key={note.id || idx} sx={{ borderRadius: 2, boxShadow: 1, p: 1 }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, maxWidth: 180, wordBreak: 'break-word', flex: 1 }}>
                  {note.content}
                </Typography>
                <IconButton
                  aria-label="Delete note"
                  color="error"
                  size="small"
                  onClick={() => onDeleteNote(note.id)}
                  disabled={deletingIdx === idx || loading}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', background: theme => theme.palette.grey[100], p: 0.5, borderRadius: 1, mt: 0.5 }}>
                <Chip label={`Time: ${note.time || '-'}`} color="primary" size="small" />
                <Chip label={`Author: ${note.author || '-'}`} color="secondary" size="small" />
              </Box>
            </CardContent>
          </Card>
        ))}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%', alignItems: 'stretch' }}>
          {/* Content field (2/3 width) */}
          <TextField
            label="Content"
            value={newNoteContent}
            onChange={e => setNewNoteContent(e.target.value)}
            multiline
            minRows={2}
            size="small"
            sx={{ flex: 2, mb: 0, height: '100%' }}
            InputProps={{ style: { height: '100%' } }}
          />
          {/* Title and button (1/3 width, stacked) */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={loading || !newNoteContent.trim() || !currentQueryParams}
              size="small"
              sx={{ mt: 1 }}
            >
              {loading ? 'Adding...' : 'Add Note'}
            </Button>
          </Box>
        </Box>
        {error && <Typography color="error" variant="caption">{error}</Typography>}
        {success && <Typography color="success.main" variant="caption">Note added!</Typography>}
      </Stack>
    </Box>
  );
};

const AnnotationsTab: React.FC<{ annotations: Annotation[], onDeleteAnnotation?: (annotation: Annotation) => void }> = ({ annotations, onDeleteAnnotation }) => {
  const getAnnotationTypeLabel = (type: string) => {
    switch (type) {
      case 'text': return 'Text';
      case 'highlight': return 'Highlight';
      case 'shapes': return 'Shape';
      case 'freehand': return 'Freehand';
      default: return type;
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Annotations ({annotations.length})
      </Typography>
      
      {annotations.length === 0 ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          backgroundColor: 'grey.50',
          borderRadius: 1,
          border: '1px dashed grey.300'
        }}>
          <Typography variant="body2" color="text.secondary">
            No annotations found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
          {annotations.map((annotation, index) => (
            <Box
              key={annotation.id}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'white',
                borderRadius: 1,
                border: '1px solid grey.200',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={getAnnotationTypeLabel(annotation.type)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    #{index + 1}
                  </Typography>
                </Box>
                {onDeleteAnnotation && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteAnnotation(annotation)}
                    sx={{ ml: 1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Type:</strong> {getAnnotationTypeLabel(annotation.type)}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Author:</strong> {annotation.createdBy || 'Unknown'}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Created:</strong> {formatDate(annotation.createdAt)}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Page:</strong> {annotation.page || 1}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Position:</strong> X: {Math.round(annotation.x)}, Y: {Math.round(annotation.y)}
              </Typography>
              
              {annotation.type === 'text' && (annotation as any).text && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Text:</strong> {(annotation as any).text}
                </Typography>
              )}
              
              {(annotation.type === 'highlight' || annotation.type === 'shapes' || annotation.type === 'freehand') && (annotation as any).note && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Note:</strong> {(annotation as any).note}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary">
                <strong>Style:</strong> Color: {annotation.style.color}, Thickness: {annotation.style.thickness}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const DocumentViewer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { drawerOpen, openDrawer, closeDrawer } = useDrawer();
  const [documentUrl, setDocumentUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);
  const [error, setError] = useState('');
  const [editingAnnotation, setEditingAnnotation] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [newAnnotationText, setNewAnnotationText] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [currentQueryParams, setCurrentQueryParams] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [documentUrlInput, setDocumentUrlInput] = useState('');
  const [rawInfo, setRawInfo] = useState<string>('');
  const [rawNotes, setRawNotes] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [previewInfo, setPreviewInfo] = useState<DocumentPreviewInfo | null>(null);

  // Estado para AI Analysis y Chat
  const [documentAnalysis, setDocumentAnalysis] = useState<DocumentAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [chatSession, setChatSession] = useState<ChatSession | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  // Estado para modal de guardado de anotaciones
  const [showSaveProgress, setShowSaveProgress] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveCurrentAnnotation, setSaveCurrentAnnotation] = useState(0);
  const [saveTotalAnnotations, setSaveTotalAnnotations] = useState(0);
  const [saveStatus, setSaveStatus] = useState('');


  // Function to filter essential parameters only
  const filterEssentialParams = (params: URLSearchParams): string => {
    const essentialParams = new URLSearchParams();
    
    // Only include these essential parameters
    const essentialKeys = ['get', 'pVersion', 'contRep', 'docId', 'accessMode', 'authId'];
    
    essentialKeys.forEach(key => {
      const value = params.get(key);
      if (value !== null) {
        // Clean the value of any extra spaces or malformed characters
        const cleanValue = value.trim().replace(/\s+/g, '');
        essentialParams.append(key, cleanValue);
        console.log(`🔧 Cleaned ${key}:`, cleanValue);
        console.log(`🔧 Original ${key}:`, value);
      } else if (key === 'get' && params.has('get')) {
        // Special case for 'get' parameter which might not have a value
        essentialParams.append(key, '');
        console.log(`🔧 Cleaned ${key}: (empty value)`);
      }
    });
    
    const filteredString = essentialParams.toString();
    console.log('🔧 Filtered essential parameters:', filteredString);
    return filteredString;
  };

  // Function to create backend URL with compId
  const createBackendUrl = (baseParams: string, compId: string): string => {
    const baseUrl = `${config.apiBaseUrl}/api/sap`;
    const fullUrl = `${baseUrl}?${baseParams}&compId=${compId}`;
    console.log('🔗 Created backend URL:', fullUrl);
    console.log('🔗 Base URL from config:', config.apiBaseUrl);
    console.log('🔗 Base params:', baseParams);
    console.log('🔗 CompId:', compId);
    return fullUrl;
  };

  // Function to parse XML and extract parameters
  const parseXmlAndExtractParams = (xmlString: string): string => {
    try {
      console.log('🔍 Starting XML parsing...');
      console.log('🔍 Raw XML string length:', xmlString.length);
      
      // Clean up malformed URL encoding before decoding
      let cleanedXml = xmlString;
      cleanedXml = cleanedXml.replace(/%%20/g, '%20'); // Fix double-encoded spaces
      cleanedXml = cleanedXml.replace(/%%/g, '%'); // Fix double-encoded %
      cleanedXml = cleanedXml.replace(/%20/g, ' '); // Convert %20 to space
      cleanedXml = cleanedXml.replace(/%3A/g, ':'); // Convert %3A to :
      cleanedXml = cleanedXml.replace(/%2F/g, '/'); // Convert %2F to /
      cleanedXml = cleanedXml.replace(/%3F/g, '?'); // Convert %3F to ?
      cleanedXml = cleanedXml.replace(/%26/g, '&'); // Convert %26 to &
      cleanedXml = cleanedXml.replace(/%3D/g, '='); // Convert %3D to =
      console.log('🔍 Cleaned XML before decode:', cleanedXml.substring(0, 200) + '...');
      
      // Decode the XML string
      const decodedXml = decodeURIComponent(cleanedXml);
      console.log('🔍 Decoded XML length:', decodedXml.length);
      console.log('🔍 Decoded XML preview:', decodedXml.substring(0, 200) + '...');
      
      // Extract URL from the XML - handle both encoded and unencoded URLs
      const urlMatch = decodedXml.match(/url="([^"]+)"/);
      if (urlMatch && urlMatch[1]) {
        let fullUrl = urlMatch[1];
        console.log('🔗 Extracted URL from XML:', fullUrl);
        console.log('🔗 URL length:', fullUrl.length);
        
        // Handle HTML entities in the URL (&amp; -> &)
        fullUrl = fullUrl.replace(/&amp;/g, '&');
        console.log('🔧 Decoded HTML entities:', fullUrl);
        
        // Clean up any malformed parameters
        fullUrl = fullUrl.replace(/%\s+/g, '%'); // Remove spaces after % in URL encoding
        fullUrl = fullUrl.replace(/\s+/g, ''); // Remove any remaining spaces
        console.log('🔧 Cleaned URL:', fullUrl);
        
        // Parse the URL to extract query parameters
        const url = new URL(fullUrl);
        const params = new URLSearchParams(url.search);
        console.log('📋 All parameters found:', Array.from(params.entries()));
        
        // Debug: Show all parameters found
        console.log('📋 All parameters found in URL:');
        Array.from(params.entries()).forEach(([key, value]) => {
          console.log(`   ${key}: ${value}`);
        });
        
        // Filter to only essential parameters
        const filteredParams = filterEssentialParams(params);
        console.log('📋 Filtered essential parameters:', filteredParams);
        
        return `?${filteredParams}`;
      } else {
        console.warn('⚠️ No URL found in XML');
        console.log('🔍 XML content:', decodedXml);
        return '';
      }
    } catch (error) {
      console.error('❌ Error parsing XML:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
      return '';
    }
  };

  // Check for query parameters on component mount
  useEffect(() => {
    const queryString = searchParams.toString();
    console.log('🔍 DocumentViewer mounted with queryString:', queryString);
    console.log('🔍 Current pathname:', window.location.pathname);
    console.log('🔍 Is document-viewer route?', window.location.pathname.includes('/document-viewer'));
    console.log('🔍 User authenticated?', !!user);
    console.log('🔍 Has XML param?', queryString.includes('xml='));
    console.log('🔍 Has docId param?', queryString.includes('docId='));
    console.log('🔍 Has get param?', queryString.includes('get&'));
    console.log('🔍 Has pVersion param?', queryString.includes('pVersion='));
    console.log('🔍 Query string length:', queryString.length);
    console.log('🔍 Query string preview:', queryString.substring(0, 100) + '...');
    
    if (queryString) {
      // Check if we have XML parameter (from /start route)
      const xmlParam = searchParams.get('xml');
      if (xmlParam) {
        console.log('📄 Processing XML parameter');
        console.log('📄 Raw XML parameter:', xmlParam);
        const extractedParams = parseXmlAndExtractParams(xmlParam);
        if (extractedParams) {
          setCurrentQueryParams(extractedParams);
          // Extract docId from the new parameters for display
          const newParams = new URLSearchParams(extractedParams.substring(1));
          setDocumentUrl(`SAP Document: ${newParams.get('docId') || 'Unknown'}`);
          // Auto-load the document with extracted parameters
          loadDocumentFromQueryParams(extractedParams);
        } else {
          setError('Error parsing XML parameters');
        }
      } else {
        // Regular query parameters - filter to essential ones
        console.log('📄 Processing direct parameters');
        const params = new URLSearchParams(queryString);
        const filteredParams = filterEssentialParams(params);
        setCurrentQueryParams(`?${filteredParams}`);
      setDocumentUrl(`SAP Document: ${searchParams.get('docId') || 'Unknown'}`);
        console.log('📄 Filtered parameters:', filteredParams);
        console.log('📄 Document ID:', searchParams.get('docId'));
        // Auto-load the document with filtered parameters
        loadDocumentFromQueryParams(`?${filteredParams}`);
      }
    } else {
      console.log('⚠️ No query parameters found');
      console.log('⚠️ This might be a redirect issue');
      
      // If this is a document-viewer route but no XML parameter, it might be a redirect issue
      if (window.location.pathname.includes('/document-viewer')) {
        console.log('⚠️ document-viewer route without XML parameter - possible redirect issue');
        setError('No XML parameter found in document-viewer route. Please check the URL.');
      }
    }
  }, [searchParams, user]);



  // Function to map backend calls with compId
  const mapBackendCall = async (queryParams: string, compId: string) => {
    // Extract essential parameters
    const params = new URLSearchParams(queryParams.substring(1));
    const essentialParams = filterEssentialParams(params);
    
    // Add compId to the parameters
    const paramsWithCompId = `${essentialParams}&compId=${compId}`;
    console.log('🔗 Mapped backend call params:', paramsWithCompId);
    console.log('🔗 Base URL from config:', config.apiBaseUrl);
    console.log('🔗 Essential params:', essentialParams);
    console.log('🔗 CompId:', compId);
    
    return paramsWithCompId;
  };

  const loadDocumentFromQueryParams = async (queryParams: string) => {
    if (!queryParams) {
      setError('Please provide document parameters');
      return;
    }
    setLoading(true);
    setError('');
    
    console.log('🔄 Loading document from query params:', queryParams);
    
    // Initialize with empty values
    let infoResponse = '';
    let docBinary: ArrayBuffer | null = null;
    let notesResponse = '';
    let annotationsResponse: Annotation[] = [];
    
    // Load metadata independently
    try {
      const metadataUrl = await mapBackendCall(queryParams, 'data');
      infoResponse = await apiService.getDocumentMetadataRaw(metadataUrl);
      console.log('📋 Metadata loaded successfully');
    } catch (error) {
      console.log('📋 Error loading metadata:', error);
      infoResponse = 'Error loading metadata';
    }
    
    // Load document data independently
    let documentResponse: any = null;
    try {
      const documentUrl = await mapBackendCall(queryParams, 'data');
      documentResponse = await apiService.getDocumentBinary(documentUrl);
      docBinary = documentResponse.data;
      setPreviewInfo(documentResponse.previewInfo || null);
      
      console.log('📄 Document binary received:', {
        byteLength: docBinary?.byteLength || 0,
        type: typeof docBinary,
        isArrayBuffer: docBinary instanceof ArrayBuffer,
        isPreview: documentResponse.previewInfo?.isPreview || false
      });
      
      if (documentResponse.previewInfo) {
        console.log('📄 Preview info:', documentResponse.previewInfo);
      }
    } catch (error) {
      console.log('📄 Error loading document data:', error);
      setError('Error loading document data');
      setLoading(false);
      return;
    }
    
    // Notes are now stored locally only - no backend loading
    console.log('📝 Using local notes storage only');
      notesResponse = ''; // Empty string for no notes
    
    // Annotations are now stored locally only - no backend loading
    console.log('🔷 Using local annotations storage only');
      annotationsResponse = []; // Empty array for no annotations
    
    // Set all data
    setRawInfo(infoResponse);
    setRawNotes(notesResponse);
    setAnnotations(annotationsResponse);
    
    // Load annotations from localStorage
    const annotationsStorageKey = `annotations_${queryParams}`;
    const savedAnnotations = localStorage.getItem(annotationsStorageKey);
    if (savedAnnotations) {
      try {
        const parsedAnnotations = JSON.parse(savedAnnotations);
        setAnnotations(parsedAnnotations);
        console.log('🔷 Loaded annotations from localStorage:', parsedAnnotations.length);
      } catch (error) {
        console.log('🔷 Error loading annotations from localStorage:', error);
      }
    } else {
      console.log('🔷 No saved annotations found in localStorage');
    }
    
    // Load notes from localStorage
    const notesStorageKey = `notes_${queryParams}`;
    const savedNotes = localStorage.getItem(notesStorageKey);
    if (savedNotes) {
      try {
        const parsedNotes = JSON.parse(savedNotes);
        setNotes(parsedNotes);
        console.log('📝 Loaded notes from localStorage:', parsedNotes.length);
      } catch (error) {
        console.log('📝 Error loading notes from localStorage:', error);
      }
    } else {
      console.log('📝 No saved notes found in localStorage');
    }
    
    const newDocumentData = {
      id: 'doc',
      title: 'Document',
      url: '',
      type: 'application/pdf',
      size: `${docBinary?.byteLength || 0} bytes`,
      lastModified: new Date().toISOString(),
      status: 'online',
      binaryData: docBinary || undefined,
    };
    
    console.log('📄 Setting document data:', newDocumentData);
    console.log('📄 docBinary byteLength:', docBinary?.byteLength);
    setDocumentData(newDocumentData);
    setCurrentQueryParams(queryParams);
    
    // Clear preview info when loading new document
    if (!documentResponse.previewInfo) {
      setPreviewInfo(null);
    }
    
    // Clear AI analysis when loading new document
    setDocumentAnalysis(null);
    setAnalysisError(null);
    setChatSession(null);
    setIsChatOpen(false);
    setChatError(null);
    
    console.log('✅ Document data set successfully');
    setLoading(false);
    
    // Note: Analysis will be triggered by useEffect when documentData changes
  };

  const loadDocument = async () => {
    if (!documentUrlInput.trim()) {
      setError('Please enter a document URL');
      return;
    }

    // Try to parse as URL first, then as direct query parameters
    const queryParams = apiService.parseDocumentViewerUrl(documentUrlInput);
    if (queryParams) {
      await loadDocumentFromQueryParams(queryParams);
    } else {
      setError('Invalid document URL format');
    }
  };

  const downloadDocument = async () => {
    if (!currentQueryParams) {
      setError('No document loaded');
      return;
    }

    try {
      const documentResponse = await apiService.getDocumentBinary(currentQueryParams);
      
      // Create blob and download
      const blob = new Blob([documentResponse.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentData?.title || 'document'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Error downloading document. Please try again.');
    }
  };

  // These functions are no longer needed as SmartDocumentViewer handles annotations
  // const addAnnotation = async () => { ... }
  // const updateAnnotation = async (id: string) => { ... }
  // const deleteAnnotation = async (id: string) => { ... }

  const addNote = async () => {
    if (!newNoteContent.trim() || !currentQueryParams) return;

    try {
      console.log('📝 Creating note with local storage...');
      
      // Show beautiful loading state
      setError(''); // Clear any previous errors
      
      // Simulate processing
      await mockBackendDelay(300);
      console.log('💾 Saving note locally...');
      
      await mockBackendDelay(500);
      console.log('📝 Processing note content...');
      
      await mockBackendDelay(400);
      console.log('✅ Note saved to local storage!');
      
      // Create note object
      const newNote = {
        id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        content: newNoteContent,
        author: user?.username || 'Current User',
        timestamp: new Date().toISOString(),
        time: new Date().toLocaleTimeString(),
        compId: 'note' // Required property for Note type
      };

      // Add to local state
      const updatedNotes = [...notes, newNote];
      setNotes(updatedNotes);
      
      // Save to localStorage
      const storageKey = `notes_${currentQueryParams}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedNotes));
      
      setNewNoteContent('');
      
      console.log('🎉 Note saved locally and to localStorage!');
      
    } catch (error) {
      console.error('❌ Error creating note:', error);
      setError('Error creating note. Please try again.');
    }
  };

  const updateNote = async (id: string) => {
    if (!currentQueryParams) return;

    try {
      console.log('✏️ Updating note with local storage...');
      
      // Show beautiful loading state
      setError(''); // Clear any previous errors
      
      // Simulate processing
      await mockBackendDelay(250);
      console.log('💾 Updating note locally...');
      
      await mockBackendDelay(400);
      console.log('📝 Processing updated note content...');
      
      await mockBackendDelay(350);
      console.log('✅ Note updated in local storage!');
      
      // Create updated note object
      const updatedNote = {
        id: id,
        content: newNoteContent,
        author: user?.username || 'Current User',
        timestamp: new Date().toISOString(),
        time: new Date().toLocaleTimeString(),
        compId: 'note' // Required property for Note type
      };

      // Update local state
      const updatedNotes = notes.map(note => 
        note.id === id ? updatedNote : note
      );
      setNotes(updatedNotes);
      
      // Save to localStorage
      const storageKey = `notes_${currentQueryParams}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedNotes));
      
      setEditingNote(null);
      setNewNoteContent('');
      
      console.log('🎉 Note updated locally and in localStorage!');
      
    } catch (error) {
      console.error('❌ Error updating note:', error);
      setError('Error updating note. Please try again.');
    }
  };

  const getDocumentType = (type: string): string => {
    if (type.toLowerCase().includes('pdf')) return 'PDF';
    if (type.toLowerCase().includes('tiff') || type.toLowerCase().includes('tif')) return 'TIFF';
    if (type.toLowerCase().includes('jpg') || type.toLowerCase().includes('jpeg')) return 'JPEG';
    if (type.toLowerCase().includes('png')) return 'PNG';
    return type.toUpperCase();
  };

  const renderDocumentViewer = () => {
    if (!documentData) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          backgroundColor: '#f5f5f5'
        }}>
          <Typography variant="h6" color="text.secondary">
            No document loaded
          </Typography>
        </Box>
      );
    }

    // Use SmartDocumentViewer for PDF, images and tiff
    return (
      <Box style={{ height: 'calc(100vh - 64px)', overflow: 'auto' }}>
        <SmartDocumentViewer
          binaryData={documentData.binaryData ?? null}
          type={documentData.type}
          onDownloadWithAnnotations={handleDownloadWithAnnotations}
          onDownloadWithoutAnnotations={handleDownloadWithoutAnnotations}
          onPrintWithAnnotations={handlePrintWithAnnotations}
          onPrintWithoutAnnotations={handlePrintWithoutAnnotations}
          onSaveAnnotations={saveAllAnnotations}
          annotations={annotations}
          onAnnotationsChange={setAnnotations}
          previewInfo={previewInfo}
          isAdmin={false} // TODO: Implement proper admin check based on user permissions
        />
      </Box>
    );
  };

  // Function to reload notes only - now just local storage
  const reloadNotes = async (queryParams: string) => {
    console.log('📝 Notes are stored locally only - no backend reload needed');
    // Notes are managed locally, no backend interaction
  };

  // Mock function to simulate backend delay
  const mockBackendDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Function to save all annotations with beautiful mock
  const saveAllAnnotations = async () => {
    if (!currentQueryParams) {
      setError('No document context');
      return;
    }
    
    try {
      console.log('💾 Saving all annotations:', annotations);
      console.log('📊 Number of annotations to save:', annotations.length);
      
      // Show beautiful progress modal
      setShowSaveProgress(true);
      setSaveProgress(0);
      setSaveCurrentAnnotation(0);
      setSaveTotalAnnotations(annotations.length);
      setSaveStatus('🚀 Initializing save process...');
      
      // Simulate backend connection
      await mockBackendDelay(500);
      setSaveProgress(5);
      setSaveStatus('🔗 Connecting to backend...');
      
      await mockBackendDelay(800);
      setSaveProgress(10);
      setSaveStatus('🔍 Validating annotations...');
      
      if (annotations.length === 0) {
        console.log('ℹ️ No annotations to save, cleaning up...');
        setSaveStatus('🧹 No annotations to save, cleaning up...');
        setSaveProgress(30);
        
        await mockBackendDelay(1000);
        setSaveProgress(60);
        setSaveStatus('🗑️ Deleting existing annotations...');
        
        await mockBackendDelay(1200);
        setSaveProgress(90);
        setSaveStatus('✨ Cleanup completed!');
        
        await mockBackendDelay(500);
        setSaveProgress(100);
        setSaveStatus('🎉 Save completed successfully!');
        
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowSaveProgress(false);
        }, 2000);
        
        return;
      }
      
      // Simulate delete process
      setSaveStatus('🗑️ Deleting existing annotations...');
      setSaveProgress(15);
      await mockBackendDelay(1000);
      setSaveProgress(20);
      setSaveStatus('✅ Cleanup completed');
      
      // Simulate save process with realistic progress
      setSaveStatus('📝 Saving annotations...');
      
      for (let i = 0; i < annotations.length; i++) {
        const annotation = annotations[i];
        console.log(`📝 Processing annotation ${i + 1}/${annotations.length}:`, annotation);
        
        setSaveCurrentAnnotation(i + 1);
        setSaveStatus(`💾 Saving annotation ${i + 1} of ${annotations.length}...`);
        
        // Simulate processing time based on annotation complexity
        const processingTime = annotation.type === 'text' ? 800 : 1200;
        await mockBackendDelay(processingTime);
        
        // Realistic progress calculation
        const baseProgress = 20; // After cleanup
        const saveProgress = (i + 1) / annotations.length * 70; // 70% for saving
        const totalProgress = baseProgress + saveProgress;
        
        setSaveProgress(Math.round(totalProgress));
        
        // Add some variety to status messages
        const statusMessages = [
          `✨ Annotation ${i + 1} saved successfully`,
          `🎯 Processing annotation ${i + 1}...`,
          `💫 Validating annotation ${i + 1}...`,
          `🚀 Uploading annotation ${i + 1}...`
        ];
        
        if (i < annotations.length - 1) {
          setSaveStatus(statusMessages[i % statusMessages.length]);
        }
      }
      
      // Final steps
      await mockBackendDelay(800);
      setSaveProgress(95);
      setSaveStatus('🔄 Finalizing save process...');
      
      // Save to localStorage
      const annotationsStorageKey = `annotations_${currentQueryParams}`;
      localStorage.setItem(annotationsStorageKey, JSON.stringify(annotations));
      console.log('💾 Annotations saved to localStorage');
      
      await mockBackendDelay(600);
      setSaveProgress(100);
      setSaveStatus('🎉 All annotations saved successfully!');
      
      console.log('✅ Mock save completed successfully');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowSaveProgress(false);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error in mock save:', error);
      setError('Error saving annotations. Please try again.');
      setSaveStatus('❌ Error saving annotations!');
      
      // Close modal after 3 seconds in case of error
      setTimeout(() => {
        setShowSaveProgress(false);
      }, 3000);
    }
  };

  // Function to delete a single annotation
  const handleDeleteAnnotation = (annotation: Annotation) => {
    console.log('🗑️ DocumentViewer - Deleting annotation:', annotation.id, annotation.type);
    console.log('📊 DocumentViewer - Current annotations count before delete:', annotations.length);
    console.log('📊 DocumentViewer - All annotations IDs:', annotations.map(a => a.id));
    
    const updatedAnnotations = annotations.filter(a => a.id !== annotation.id);
    console.log('📊 DocumentViewer - Annotations count after delete:', updatedAnnotations.length);
    console.log('📊 DocumentViewer - Remaining annotations IDs:', updatedAnnotations.map(a => a.id));
    
    setAnnotations(updatedAnnotations);
    
    // Update localStorage
    if (currentQueryParams) {
      const annotationsStorageKey = `annotations_${currentQueryParams}`;
      localStorage.setItem(annotationsStorageKey, JSON.stringify(updatedAnnotations));
      console.log('💾 Updated annotations in localStorage');
    }
    
    console.log('✅ DocumentViewer - Annotation deleted from state and localStorage');
  };

  // Export/Print handlers
  const handleDownloadWithAnnotations = async () => {
    if (!documentData?.binaryData || !documentData.type.toLowerCase().includes('pdf')) {
      alert('Only PDF export is supported for now.');
      return;
    }
    try {
      const pdfjsLib = await import('pdfjs-dist');
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;

      // Configura el worker local (debe estar en public/pdf.worker.mjs)
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

      // Haz una copia del ArrayBuffer para evitar detached
      const binaryDataCopy = documentData.binaryData.slice(0);
      const pdf = await pdfjsLib.getDocument({ data: binaryDataCopy }).promise;
      const totalPages = pdf.numPages;
      const pageImages: Array<{ dataUrl: string; width: number; height: number }> = [];

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');
        await page.render({ canvasContext: ctx, viewport }).promise;
        // Draw annotations of this page on the canvas
        drawAnnotationsOnCanvas(ctx, annotations, viewport);
        pageImages.push({
          dataUrl: canvas.toDataURL('image/jpeg', 1.0),
          width: viewport.width,
          height: viewport.height
        });
      }
      // Create multi-page PDF
      const pdfDoc = new jsPDF({ unit: 'px', format: [pageImages[0].width, pageImages[0].height] });
      pageImages.forEach((img, idx) => {
        if (idx > 0) pdfDoc.addPage([img.width, img.height], 'portrait');
        pdfDoc.addImage(img.dataUrl, 'JPEG', 0, 0, img.width, img.height);
      });
      pdfDoc.save(`${documentData.title || 'document'}-with-annotations.pdf`);
    } catch (error) {
      console.error('Error exporting PDF with annotations:', error);
      alert('Error exporting PDF with annotations: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  // Utility function to draw annotations on a canvas
  function drawAnnotationsOnCanvas(ctx: CanvasRenderingContext2D, annotations: any[], viewport: any) {
    annotations.forEach(annotation => {
      // For viewer annotations, draw according to type
      if (annotation.type === 'text' && annotation.text) {
        ctx.save();
        ctx.font = '14px Arial';
        ctx.fillStyle = '#FF0000';
        ctx.fillText(annotation.text, annotation.x, annotation.y + 14);
        ctx.restore();
      }
    });
  }

  const handleDownloadWithoutAnnotations = async () => {
    if (!documentData?.binaryData) return;
    const blob = new Blob([documentData.binaryData], { type: documentData.type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = documentData.title || 'document';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };
  const handlePrintWithAnnotations = async () => {
    // TODO: Implement printing with annotations
    alert('Print with annotations: coming soon!');
  };
  const handlePrintWithoutAnnotations = async () => {
    if (!documentData?.binaryData) return;
    const blob = new Blob([documentData.binaryData], { type: documentData.type });
    const url = window.URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => win.print();
    }
  };

  // Funciones para AI Analysis y Chat
  const handleAnalyzeDocument = async () => {
    console.log('🔍 handleAnalyzeDocument called');
    console.log('🔍 documentData:', documentData);
    console.log('🔍 documentData?.binaryData:', documentData?.binaryData);
    console.log('🔍 documentData?.binaryData?.byteLength:', documentData?.binaryData?.byteLength);
    
    if (!documentData?.binaryData) {
      console.log('❌ No document data available');
      setAnalysisError('No document loaded to analyze');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);
    setDocumentAnalysis(null);

    try {
      console.log('🤖 Iniciando análisis de documento...');
      const analysis = await geminiService.analyzeDocument(documentData.binaryData);
      console.log('📊 Setting documentAnalysis state (manual):', analysis);
      setDocumentAnalysis(analysis);
      console.log('✅ Análisis completado:', analysis);
    } catch (error) {
      console.error('❌ Error en análisis:', error);
      setAnalysisError('Error analyzing document. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Auto-analyze document when documentData changes
  useEffect(() => {
    if (documentData?.binaryData && !documentAnalysis && !isAnalyzing && !analysisError) {
      console.log('🤖 Auto-analyzing document due to documentData change');
      setTimeout(async () => {
        console.log('🔍 Auto-analysis triggered');
        console.log('🔍 documentData:', documentData);
        console.log('🔍 documentData?.binaryData:', documentData?.binaryData);
        console.log('🔍 documentData?.binaryData?.byteLength:', documentData?.binaryData?.byteLength);
        
        if (!documentData?.binaryData) {
          console.log('❌ No document data available');
          setAnalysisError('No document loaded to analyze');
          return;
        }

        setIsAnalyzing(true);
        setAnalysisError(null);
        setDocumentAnalysis(null);

        try {
          console.log('🤖 Iniciando análisis de documento...');
          const analysis = await geminiService.analyzeDocument(documentData.binaryData);
          console.log('📊 Setting documentAnalysis state (auto):', analysis);
          setDocumentAnalysis(analysis);
          console.log('✅ Análisis completado:', analysis);
        } catch (error) {
          console.error('❌ Error en análisis:', error);
          setAnalysisError('Error analyzing document. Please try again.');
        } finally {
          setIsAnalyzing(false);
        }
      }, 1000);
    }
  }, [documentData, documentAnalysis, isAnalyzing, analysisError]);

  // Monitor documentAnalysis changes
  useEffect(() => {
    console.log('📊 documentAnalysis state changed:', documentAnalysis);
  }, [documentAnalysis]);



  const handleStartChat = async () => {
    if (!documentData?.binaryData) {
      setChatError('No document loaded for chat');
      return;
    }

    setIsChatLoading(true);
    setChatError(null);
    setIsChatOpen(true); // Abrir el modal inmediatamente

    try {
      console.log('💬 Iniciando sesión de chat...');
      const session = await geminiService.startChat(documentData.binaryData);
      setChatSession(session);
      console.log('✅ Chat iniciado:', session.id);
    } catch (error) {
      console.error('❌ Error iniciando chat:', error);
      setChatError('Error starting chat. Please try again.');
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleSendMessage = async (message: string): Promise<ChatMessage | null> => {
    if (!chatSession) return null;

    setIsChatLoading(true);
    setChatError(null);

    try {
      console.log('💬 Enviando mensaje al AI:', message);
      
      // Obtener la respuesta del AI
      const aiMessage = await geminiService.sendMessage(chatSession, message);
      
      console.log('✅ Respuesta del AI recibida');
      return aiMessage;
    } catch (error) {
      console.error('❌ Error enviando mensaje:', error);
      setChatError('Error sending message. Please try again.');
      return null;
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setChatSession(null);
    setChatError(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 450,
            p: 4,
            borderRadius: 3,
            backgroundColor: 'white',
            boxShadow: 4,
            border: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <Typography 
            variant="h5" 
            color="primary" 
            gutterBottom
            sx={{ mb: 3, fontWeight: 600 }}
          >
            Loading document...
          </Typography>
          <Typography 
            variant="h6" 
            color="textSecondary" 
            gutterBottom
            sx={{ mb: 4, fontWeight: 400 }}
          >
            Just a moment, we are fetching your document, notes and annotations.
          </Typography>
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress 
              variant="indeterminate"
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                }
              }}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Almost there...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Menu items for the drawer
  const menuItems = [
    {
      id: 'viewer',
      label: 'Document Viewer',
      icon: <DashboardIcon />,
      path: '/viewer',
      description: 'View documents and files'
    },
    {
      id: 'workflow',
      label: 'Workflow',
      icon: <GroupIcon />,
      path: '/workflow',
      description: 'Manage workflows'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: <SecurityIcon />,
      path: '/notifications',
      description: 'View notifications'
    },
    {
      id: 'go-metadata',
      label: 'Metadata',
      icon: <AccountCircleIcon />,
      path: '/go-metadata',
      description: 'Document metadata'
    },
    {
      id: 'dashboard',
      label: 'Admin Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      description: 'Admin overview and validation'
    },
    {
      id: 'groups',
      label: 'Groups',
      icon: <GroupIcon />,
      path: '/groups',
      description: 'Manage user groups'
    },
    {
      id: 'permissions',
      label: 'Redaction Restrictions',
      icon: <SecurityIcon />,
      path: '/permissions',
      description: 'Manage redaction restrictions'
    },
    {
      id: 'users',
      label: 'Users',
      icon: <AccountCircleIcon />,
      path: '/users',
      description: 'User management'
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: <LogoutIcon />,
      path: '#logout',
      description: 'Sign out'
    },
  ];

  const handleMenuItemClick = (path: string) => {
    if (path === '#logout') {
      logout();
      navigate('/login');
      closeDrawer();
      return;
    }
    navigate(path);
    closeDrawer();
  };

  const isAdmin = user?.username === 'admin';

  return (
    <Box sx={{ height: '100vh', display: 'flex' }}>
      {/* Document Viewer - 2/3 of screen */}
      <Box sx={{ width: '66.66%', height: '100%' }}>
        {renderDocumentViewer()}
      </Box>

      {/* Sidebar - 1/3 of screen */}
      {sidebarOpen && (
        <Box sx={{ width: '33.33%', height: 'calc(100vh - 64px)', borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Upper half: Tabs */}
          <Box sx={{ height: '50%', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{ minHeight: 48 }}
            >
              <Tab icon={<InfoIcon />} label="Metadata" iconPosition="start" sx={{ minHeight: 48 }} />
              <Tab icon={<CommentIcon />} label={`Notes (${notes.length})`} iconPosition="start" sx={{ minHeight: 48 }} />
              <Tab icon={<BookmarkIcon />} label={`Annotations (${annotations.length})`} iconPosition="start" sx={{ minHeight: 48 }} />
            </Tabs>
            <TabPanel value={tabValue} index={0}>
              <MetadataTab rawInfo={rawInfo} />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <NotesTab 
                rawNotes={rawNotes} 
                currentQueryParams={currentQueryParams} 
                reloadNotes={reloadNotes} 
                localNotes={notes}
                onAddNote={addNote}
                onUpdateNote={updateNote}
                onDeleteNote={(id: string) => {
                  const updatedNotes = notes.filter(note => note.id !== id);
                  setNotes(updatedNotes);
                  if (currentQueryParams) {
                    const notesStorageKey = `notes_${currentQueryParams}`;
                    localStorage.setItem(notesStorageKey, JSON.stringify(updatedNotes));
                  }
                }}
                newNoteContent={newNoteContent}
                setNewNoteContent={setNewNoteContent}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <AnnotationsTab annotations={annotations} onDeleteAnnotation={handleDeleteAnnotation} />
            </TabPanel>
          </Box>
          <Divider />
          {/* Lower half: AI Summary */}
          <Box sx={{ height: '50%', minHeight: 0, overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
            <AISummary 
              documentData={documentData}
              onStartChat={handleStartChat}
              onAnalyzeDocument={handleAnalyzeDocument}
              analysis={documentAnalysis}
              isLoading={isAnalyzing}
              error={analysisError}
            />
          </Box>
        </Box>
      )}

      {/* Save Progress Modal */}
      {showSaveProgress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 3,
              padding: 4,
              maxWidth: 500,
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              💾 Saving Annotations
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
              {saveStatus}
            </Typography>
            
            {saveTotalAnnotations > 0 && (
              <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>
                Annotation {saveCurrentAnnotation} of {saveTotalAnnotations} ({saveProgress}%)
              </Typography>
            )}
            
            <Box sx={{ width: '100%', mb: 3 }}>
              <Box
                sx={{
                  width: '100%',
                  height: 20,
                  backgroundColor: '#f0f0f0',
                  borderRadius: 10,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    width: `${saveProgress}%`,
                    height: '100%',
                    backgroundColor: '#1976d2',
                    borderRadius: 10,
                    transition: 'width 0.3s ease',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                      animation: 'shimmer 2s infinite',
                      '@keyframes shimmer': {
                        '0%': {
                          transform: 'translateX(-100%)',
                        },
                        '100%': {
                          transform: 'translateX(100%)',
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            
            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
              Please wait while your annotations are being saved...
            </Typography>
          </Box>
        </Box>
      )}

      {/* AI Chat Interface */}
                      <AIChatInterface
                  isOpen={isChatOpen}
                  onClose={handleCloseChat}
                  session={chatSession}
                  onSendMessage={handleSendMessage}
                  onUpdateSession={setChatSession}
                  isLoading={isChatLoading}
                  error={chatError}
                />

      {/* Drawer Menu */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: 'block',
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        <Box sx={{ width: 280 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
              DataUnify Intelligent Viewer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome to DU-IV
            </Typography>
          </Box>
          
          <List sx={{ pt: 1 }}>
            {menuItems
              .filter(item => {
                // Mostrar opciones generales para todos
                if (['viewer', 'workflow', 'notifications', 'go-metadata', 'logout'].includes(item.id)) {
                  return true;
                }
                // Solo mostrar opciones de admin si el usuario es admin
                if (['dashboard', 'groups', 'permissions', 'users'].includes(item.id)) {
                  return isAdmin;
                }
                return true;
              })
              .map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleMenuItemClick(item.path)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      secondary={item.description}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 'normal',
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DocumentViewer; 